import { FC, HTMLAttributes, useRef } from "react"
import styled from "styled-components"
import Color from "../../constants/Color"
import YourVisionOurExpertise from "../molecules/YourVisionOurExpertise"
import Spacer from "../atoms/Spacer"
import * as Animation from "../../functionality/Animation"
import * as Layout from "../../functionality/Layout"
import ImageTitleText from "../molecules/ImageTitleText"
import fingerprint from "../../images/fingerprint.jpg"
import navigation from "../../images/navigation.jpg"
import coding from "../../images/coding.jpg"

interface Props extends HTMLAttributes<HTMLDivElement> {
    widthInPx: number
    screenHeightInPx: number
    startYInPx: number
}

const tweenByFractionUpScreenWhenHorizontal: Animation.VariableValue = {
    start: 0,
    end: 1,
    startControl: 0.1,
    endControl: 0.7,
}

const tweenByFractionUpScreenWhenVertical: Animation.VariableValue = {
    start: 0,
    end: 1,
    startControl: 0.1,
    endControl: 0.7,
}

const tweenForRef = (ref: React.RefObject<HTMLDivElement>, screenHeightInPx: number, layoutIsVertical: boolean) => {
    const yInPx = ref.current?.getBoundingClientRect().y
    const fraction = Animation.fractionUpScreen(screenHeightInPx, yInPx)
    return layoutIsVertical ? Animation.calculateValue(tweenByFractionUpScreenWhenVertical, fraction)
                            : Animation.calculateValue(tweenByFractionUpScreenWhenHorizontal, fraction)
}

const WhatWeDo: FC<Props> = ({widthInPx, screenHeightInPx, startYInPx}) => {

    // Refs
    const headerRef = useRef<HTMLDivElement>(null)
    const sectionOneRef = useRef<HTMLDivElement>(null)
    const sectionTwoRef = useRef<HTMLDivElement>(null)
    const sectionThreeRef = useRef<HTMLDivElement>(null)

    // The header needs to animate depending on its starting position
    const headerStartYInPx = (startYInPx + Layout.WhatWeDo.spaceAboveHeadingInPx)
    const headerFractionToUse: Animation.VariableValue = {
        start: 1,
        end: 0,
        startControl: 0,
        endControl: headerStartYInPx,
    }
    const headerYInPx = headerRef.current?.getBoundingClientRect().y
    const headerFractionUpScreen = headerYInPx ? Animation.calculateValue(headerFractionToUse, headerYInPx)
                                               : 0
    const headerTween = Animation.calculateValue(tweenByFractionUpScreenWhenHorizontal, headerFractionUpScreen)

    // Calculate heights
    const sectionWidthInPx = Animation.calculateValue(Layout.WhatWeDo.Section.widthValues, widthInPx)
    const spacerHeightInPx = Animation.calculateValue(Layout.WhatWeDo.verticalSpaceBetweenSections, widthInPx)
    const isVertical = Layout.WhatWeDo.Section.isVerticalForWidthInPx(sectionWidthInPx)

    return <Wrapper widthInPx={widthInPx}>
        <Spacer heightInPx={Layout.WhatWeDo.spaceAboveHeadingInPx}/>
        <YourVisionOurExpertise ref={headerRef} widthInPx={widthInPx} tween={headerTween}/>
        <Spacer heightInPx={Layout.WhatWeDo.spaceBelowHeadingInPx}/>
        <Content>
            <ImageTitleText ref={sectionOneRef}
                            widthInPx={sectionWidthInPx}
                            image={fingerprint}
                            imageAlt="A fingerprint"
                            title="CRAFT YOUR BRAND"
                            detail="How do you want your customers to feel when they hear your product’s name? How do you want them to describe it when recommending it to a friend? We can help you find the best way to express what makes your product unique, in a way that will make your customers want to listen."
                            tween={tweenForRef(sectionOneRef, screenHeightInPx, isVertical)}
            />
            <Spacer heightInPx={spacerHeightInPx}/>
            <ImageTitleText ref={sectionTwoRef}
                            widthInPx={sectionWidthInPx}
                            image={navigation}
                            shouldFadeImageEdge={true}
                            imageAlt="Pins on a map"
                            title="ISOLATE VALUE"
                            detail="At the start of a project everyone is excited about all of the possibilities. As work begins, we need to narrow our focus to the features that provide the most value to users. We can guide you through the iterative process of finding out what will work before you commit to building it."
                            tween={tweenForRef(sectionTwoRef, screenHeightInPx, isVertical)}
            />
            <Spacer heightInPx={spacerHeightInPx}/>
            <ImageTitleText ref={sectionThreeRef}
                            widthInPx={sectionWidthInPx}
                            image={coding}
                            shouldFadeImageEdge={true}
                            imageSaturation={0.3}
                            imageAlt="A developer writing code"
                            title="MAKE IT AWESOME"
                            detail="Everything we do is designed to make sure that we build the best possible version of your product. We don’t build blind: regular user feedback is vital to ensure that what is delivered is both useful and needed. Whatever platform you’re targeting, we have the tools and experience to make your product amazing."
                            tween={tweenForRef(sectionThreeRef, screenHeightInPx, isVertical)}
            />
            <Spacer heightInPx={spacerHeightInPx}/>
        </Content>
    </Wrapper>
}

export default WhatWeDo

const Wrapper = styled.div.attrs<{widthInPx: number}>(p => ({
    style: {
        width: p.widthInPx + "px",
        //height: Layout.WhatWeDo.totalHeightForWidthInPx(p.widthInPx) + "px",
    }
}))`
    background-color: ${Color.White};
`

const Content = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    column-gap: 200px;
    
    width: 100%;
    margin: 0 auto;
    padding: 0 10px;
    box-sizing: border-box;
`